package com.klantenstop.web.data.repos

import com.klantenstop.web.data.NetworkingConfig
import com.klantenstop.web.data.getRequest
import com.klantenstop.web.data.models.NetworkResponse
import com.klantenstop.web.data.models.dto.requests.CreateTokenDto
import com.klantenstop.web.data.models.dto.requests.ForgotPasswordDto
import com.klantenstop.web.data.models.dto.requests.ResetPasswordDto
import com.klantenstop.web.data.models.dto.responses.AuthStatusResponse
import com.klantenstop.web.data.models.dto.responses.ErrorResponse
import com.klantenstop.web.data.postRequest

object AuthRepo {
    private val oauthUrl = "${NetworkingConfig.baseUrl}/api/oauth"

    @Suppress("TooGenericExceptionThrown")
    suspend fun login(username: String, password: String) {
        val token = CreateTokenDto(username, password)

        val response: NetworkResponse<Unit, ErrorResponse> = postRequest("$oauthUrl/token", token)

        when (response) {
            // Backend sets HTTP-only cookies. Auth state refresh is handled by AuthModule.
            is NetworkResponse.Success -> return
            is NetworkResponse.ServerError -> throw RuntimeException(response.body?.errorDescription)
            is NetworkResponse.Error -> throw response.error ?: Exception("Unknown error")
        }
    }

    @Suppress("TooGenericExceptionThrown")
    suspend fun refreshToken() {
        // With cookie-based auth, we don't need to send refresh token manually
        // The backend will read it from the HTTP-only cookie
        val response: NetworkResponse<Unit, ErrorResponse> = postRequest("$oauthUrl/refresh", emptyMap<String, String>())

        when (response) {
            is NetworkResponse.Success -> return
            is NetworkResponse.ServerError -> throw RuntimeException(response.body?.errorDescription)
            is NetworkResponse.Error -> throw response.error ?: Exception("Unknown error")
        }
    }

    /**
     * Check authentication status by verifying the HTTP-only cookies on the backend
     */
    suspend fun checkAuthStatus(): AuthStatusResponse {
        val response: NetworkResponse<AuthStatusResponse, ErrorResponse> = getRequest("$oauthUrl/status")

        return when (response) {
            is NetworkResponse.Success -> response.body
            is NetworkResponse.ServerError, is NetworkResponse.Error -> AuthStatusResponse(false)
        }
    }

    /**
     * Logout by clearing cookies on the backend
     */
    suspend fun logout() {
        val response: NetworkResponse<Unit, ErrorResponse> = postRequest("$oauthUrl/logout", emptyMap<String, String>())

        when (response) {
            is NetworkResponse.Success -> return
            is NetworkResponse.ServerError -> throw RuntimeException(response.body?.errorDescription ?: "Server error")
            is NetworkResponse.Error -> throw response.error ?: Exception("Unknown error")
        }
    }

    @Suppress("TooGenericExceptionThrown")
    suspend fun forgotPassword(email: String) {
        val request = ForgotPasswordDto(email)
        val response: NetworkResponse<Unit, ErrorResponse> = postRequest("$oauthUrl/forgot-password", request)

        when (response) {
            is NetworkResponse.Success -> return
            is NetworkResponse.ServerError -> throw RuntimeException(response.body?.errorDescription ?: "Server error")
            is NetworkResponse.Error -> throw response.error ?: Exception("Unknown error")
        }
    }

    @Suppress("TooGenericExceptionThrown")
    suspend fun resetPassword(token: String, password: String, passwordConfirmation: String) {
        val request = ResetPasswordDto(token, password, passwordConfirmation)
        val response: NetworkResponse<Unit, ErrorResponse> = postRequest("$oauthUrl/reset-password", request)

        when (response) {
            is NetworkResponse.Success -> return
            is NetworkResponse.ServerError -> throw RuntimeException(response.body?.errorDescription ?: "Server error")
            is NetworkResponse.Error -> throw response.error ?: Exception("Unknown error")
        }
    }
}
